/**
 * PSJ category navigation behaviour.
 *
 * 1. Mobile accordion: collapses the category panels on load and toggles them
 *    from a dedicated disclosure button. The markup renders expanded so the
 *    hierarchy stays usable if this script never runs.
 * 2. Katalog mega menu: keeps aria-expanded in sync with the theme's CSS driven
 *    hover/focus behaviour and adds Escape-to-close. Opening stays CSS driven so
 *    there is no hover gap and no flash of unstyled menu.
 */

class PsjCategoryAccordion extends HTMLElement {
  connectedCallback() {
    this.toggles = Array.from(this.querySelectorAll('.psj-catacc__toggle'));

    this.toggles.forEach((toggle) => {
      const panel = this.panelFor(toggle);
      if (!panel) return;

      // Keep the panel holding the current page open, collapse the rest.
      const isCurrent = panel.querySelector('[aria-current="page"]') !== null;
      this.setExpanded(toggle, panel, isCurrent);

      toggle.addEventListener('click', () => {
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        this.setExpanded(toggle, panel, !expanded);
      });
    });
  }

  panelFor(toggle) {
    const id = toggle.getAttribute('aria-controls');
    return id ? this.querySelector(`#${id}`) : null;
  }

  setExpanded(toggle, panel, expanded) {
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    panel.hidden = !expanded;
  }
}

if (!customElements.get('psj-category-accordion')) {
  customElements.define('psj-category-accordion', PsjCategoryAccordion);
}

function initPsjCatalogMegaMenu() {
  document.querySelectorAll('[data-psj-catalog]').forEach((item) => {
    if (item.dataset.psjCatalogReady === 'true') return;

    const trigger = item.querySelector('.header__menu-item');
    const panel = item.querySelector('.psj-catnav-panel');
    if (!trigger || !panel) return;

    item.dataset.psjCatalogReady = 'true';

    trigger.setAttribute('aria-haspopup', 'true');
    trigger.setAttribute('aria-expanded', 'false');
    if (panel.id) trigger.setAttribute('aria-controls', panel.id);

    const setExpanded = (expanded) => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    };

    // Escape suppresses the CSS hover/focus state until the pointer or focus leaves.
    const suppress = () => {
      item.setAttribute('data-psj-closed', '');
      setExpanded(false);
    };
    const release = () => item.removeAttribute('data-psj-closed');

    item.addEventListener('pointerenter', () => {
      release();
      setExpanded(true);
    });
    item.addEventListener('pointerleave', () => {
      release();
      setExpanded(false);
    });
    item.addEventListener('focusin', () => {
      release();
      setExpanded(true);
    });
    item.addEventListener('focusout', (event) => {
      if (item.contains(event.relatedTarget)) return;
      release();
      setExpanded(false);
    });
    item.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      suppress();
      trigger.focus();
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPsjCatalogMegaMenu);
} else {
  initPsjCatalogMegaMenu();
}

// The Theme Editor re-renders the header section on every change.
document.addEventListener('shopify:section:load', initPsjCatalogMegaMenu);
